import styled, { css } from 'styled-components';

import colors from '../../../../styles/colors';

import { CyclePhase } from '../../../utils/cycle';

// Phase colours carry meaning, so they are fixed rather than themed. Estimated days are lighter than logged ones.
// Fills all clear 4.5:1 against white text.
const PHASE_COLORS = {
  [CyclePhase.MENSTRUAL]: { fill: '#C8243F', tint: '#FBE3E7' },
  [CyclePhase.FOLLICULAR]: { fill: '#2A78A6', tint: '#DDEFF8' },
  [CyclePhase.OVULATORY]: { fill: '#8A5F00', tint: '#FBF1DA' },
  [CyclePhase.LUTEAL]: { fill: '#55615C', tint: '#E5EAE7' },
};

const phaseStyles = ({ $phase, $isEstimated }) => {
  const phaseColors = PHASE_COLORS[$phase];
  if (!phaseColors) {
    // No phase (nothing anchors the cycle yet): a plain outlined day.
    return css`
      background-color: ${colors.base.alpha};
      border: 1px solid ${colors.shades.secondary7};
      color: ${colors.shades.gamma10};
    `;
  }
  return $isEstimated
    ? css`
      background-color: ${phaseColors.tint};
      border: 1px dashed ${phaseColors.fill};
      color: ${colors.base.beta};
    `
    : css`
      background-color: ${phaseColors.fill};
      border: 1px solid ${phaseColors.fill};
      color: ${colors.base.alpha};
    `;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  background-color: ${colors.base.alpha};
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`;

const CurrentPhase = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SmallLabel = styled.span`
  font-size: 11px;
  line-height: 14px;
  color: ${colors.base.beta};
`;

const PhaseHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PhaseSwatch = styled.span`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-sizing: border-box;
  ${phaseStyles}
`;

const PhaseName = styled.span`
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  color: ${colors.base.beta};
`;

const PhaseDay = styled.span`
  font-size: 18px;
  line-height: 26px;
  color: ${colors.shades.gamma10};
`;

// The phase guide inside the info icon's tooltip.
const PhaseGuide = styled.span`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 280px;
  padding: 4px 0;
`;

const PhaseGuideItem = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 16px;
`;

const PhaseGuideName = styled.span`
  font-weight: 700;
`;

// Light pill in the phase colour; dashed when estimated, like the day circles.
const StatusPill = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  ${({ $phase, $isEstimated }) => {
    const phaseColors = PHASE_COLORS[$phase];
    return phaseColors && css`
      background-color: ${phaseColors.tint};
      border: 1px ${$isEstimated ? 'dashed' : 'solid'} ${$isEstimated ? phaseColors.fill : phaseColors.tint};
      color: ${phaseColors.fill};
    `;
  }}
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 14px;
  color: ${colors.base.beta};
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
`;

// The check-in day (the last card) is tinted with its phase colour.
const DayCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 12px 6px;
  border-radius: 8px;
  background-color: ${({ $phase }) => PHASE_COLORS[$phase]?.tint ?? colors.base.gamma};
`;

const DayLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $phase }) => ($phase ? 600 : 400)};
  line-height: 14px;
  color: ${({ $phase }) => PHASE_COLORS[$phase]?.fill ?? colors.base.beta};
`;

const DateCircle = styled.span`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-sizing: border-box;
  font-size: 13px;
  font-weight: 600;
  ${phaseStyles}
`;

const DaySymptoms = styled.span`
  max-width: 100%;
  font-size: 11px;
  line-height: 14px;
  text-align: center;
  overflow-wrap: anywhere;
  color: ${colors.base.beta};
`;

const Footnote = styled.span`
  padding-top: 12px;
  border-top: 1px solid ${colors.base.gamma};
  font-size: 11px;
  line-height: 16px;
  color: ${colors.shades.gamma10};
`;

const WeekNav = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WeekRange = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 130px;
`;

const WeekRangeLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: ${colors.base.beta};
`;

const WeekRangeHint = styled.span`
  font-size: 11px;
  line-height: 14px;
  color: ${colors.shades.gamma10};
`;

const BackToCheckIn = styled.button`
  margin-left: 8px;
  padding: 0;
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.shades.gamma10};
  text-decoration: underline;
  cursor: pointer;
`;

export {
  Container,
  Header,
  CurrentPhase,
  SmallLabel,
  PhaseHeading,
  PhaseSwatch,
  PhaseName,
  PhaseDay,
  PhaseGuide,
  PhaseGuideItem,
  PhaseGuideName,
  StatusPill,
  Legend,
  LegendItem,
  WeekGrid,
  DayCard,
  DayLabel,
  DateCircle,
  DaySymptoms,
  Footnote,
  WeekNav,
  WeekRange,
  WeekRangeLabel,
  WeekRangeHint,
  BackToCheckIn,
};
